// crewbit-admin/src/hooks/useInvoiceLinkedPhotos.ts
//
// Arc C3 P2 — wiring for §Money.InvoicePhotoRenderingFromLinkedJob.
// Mirrors crewbit-mobile/hooks/useInvoiceLinkedPhotos.ts.
// Spec: docs/references/money/2026-05-31-C3-Money-Nav-Surface-Polish-Spec-v1.md §5 (P2).
//
// Read-only hook over the P1 RPC get_invoice_linked_photos. Web-runtime
// divergence: window event listener instead of DeviceEventEmitter.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { callMoneyRpc } from '../lib/moneyRpc';
import type { MoneyError } from '../lib/errorMap';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';

export interface InvoiceLinkedPhoto {
  file_url: string;
  file_name: string | null;
  item_description: string;
  created_at: string | null;
}

interface RawEnvelope {
  success: boolean;
  photos?: InvoiceLinkedPhoto[];
}

export interface UseInvoiceLinkedPhotosReturn {
  photos: InvoiceLinkedPhoto[];
  isLoading: boolean;
  error: MoneyError | null;
  refetch: () => Promise<void>;
}

export function useInvoiceLinkedPhotos(invoiceId: string | null): UseInvoiceLinkedPhotosReturn {
  const [photos, setPhotos] = useState<InvoiceLinkedPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<MoneyError | null>(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(async () => {
    if (!invoiceId) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const result = await callMoneyRpc<RawEnvelope>('get_invoice_linked_photos', {
        p_invoice_id: invoiceId,
      });
      if (!mountedRef.current) return;
      if (result.success) {
        setPhotos(result.data.photos ?? []);
        setError(null);
      } else {
        setError(result.error);
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [invoiceId]);

  // Reset when the invoice changes.
  useEffect(() => {
    setPhotos([]);
    setError(null);
  }, [invoiceId]);

  useEffect(() => {
    if (!invoiceId) return;
    void doFetch();
  }, [invoiceId, doFetch]);

  // Reload on money mutations (e.g. a photo attached to the linked job).
  useEffect(() => {
    const onMutation = () => {
      void doFetch();
    };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    return () => {
      window.removeEventListener(MONEY_MUTATION_EVENT, onMutation);
    };
  }, [doFetch]);

  const refetch = useCallback(async () => {
    await doFetch();
  }, [doFetch]);

  return useMemo(
    () => ({ photos, isLoading, error, refetch }),
    [photos, isLoading, error, refetch],
  );
}
